/**
 * triggerLogCopy — the German rendering of a trigger DECISION row for the
 * "Aktivität" panel. Pure functions (jest-testable), no React, no imports: turns a
 * SocialTriggerLog row into a one-line human sentence + a relative time.
 *
 * The decision vocabulary MUST stay in sync with the server's
 * SocialTriggerLogRepo / SocialAutomationService.onTrigger gates.
 */

// The verb + tone for each decision. `tone` drives the panel dot colour class.
const DECISION_META = {
    fired: { verb: 'Ausgelöst', tone: 'ok' },
    suppressed_recent: { verb: 'Übersprungen', tone: 'muted' },
    deduped_inflight: { verb: 'Wartet', tone: 'muted' },
    workflow_disabled: { verb: 'Übersprungen', tone: 'muted' },
    feature_disabled: { verb: 'Übersprungen', tone: 'muted' },
    error: { verb: 'Fehler', tone: 'error' },
};

/** The tone token ('ok' | 'muted' | 'error') for a decision. */
export const decisionTone = decision =>
    (DECISION_META[decision] && DECISION_META[decision].tone) || 'muted';

/**
 * A one-line German sentence for a decision row: "<Verb> – <detail>".
 * Falls back to a readable default per decision when `detail` is absent.
 * @param {{decision:string, detail?:string}} row
 * @returns {string}
 */
export const decisionText = row => {
    if (!row || !row.decision) return '';
    const meta = DECISION_META[row.decision] || { verb: row.decision };
    const detail = (row.detail && String(row.detail).trim()) || defaultDetail(row.decision);
    return detail ? `${meta.verb} – ${detail}` : meta.verb;
};

const defaultDetail = decision => {
    switch (decision) {
        case 'fired':
            return 'Beitrag erstellt';
        case 'suppressed_recent':
            return 'kürzlich bereits ausgelöst';
        case 'deduped_inflight':
            return 'vorheriger Beitrag noch in Arbeit';
        case 'workflow_disabled':
            return 'Workflow ist deaktiviert';
        case 'feature_disabled':
            return 'Automation ist ausgeschaltet';
        default:
            return '';
    }
};

/**
 * A compact German relative time for a timestamp ("gerade eben", "vor 5 Min.",
 * "vor 2 Std.", "vor 3 Tagen"). `now` is injectable for deterministic tests.
 * @param {string|number|Date} when
 * @param {number} [now=Date.now()]
 * @returns {string}
 */
export const relTime = (when, now = Date.now()) => {
    const t = when instanceof Date ? when.getTime() : new Date(when).getTime();
    if (!Number.isFinite(t)) return '';
    const secs = Math.max(0, Math.round((now - t) / 1000));
    if (secs < 45) return 'gerade eben';
    const mins = Math.round(secs / 60);
    if (mins < 60) return `vor ${mins} Min.`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    const days = Math.round(hours / 24);
    return days === 1 ? 'vor 1 Tag' : `vor ${days} Tagen`;
};
